import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { AddTicketNumberSequenceMutation as AddTicketNumberSequence } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddTicketNumberSequenceMutation from './AddTicketNumberSequenceMutation';
import UI from '../../../common/UI';
import useIntegerInput from '../../../common/useIntegerInput';
import useMutation from '../../../api/useMutation';
import useValidation from '../../../common/useValidation';

const addTicketNumberSequenceSchema = yup.object().shape({
  title: yup.string().ensure().required(),
  start_number: yup.number().min(1).required(),
});

interface Event {
  id: string;
}

export interface AddTicketNumberSequenceFormProps {
  event: Event;
  onSuccess?: (sequence: AddTicketNumberSequence['addTicketNumberSequence']) => void;
}

const AddTicketNumberSequenceForm = (
  { event, onSuccess }: AddTicketNumberSequenceFormProps,
) => {
  const { t } = useTranslation();
  const integerInput = useIntegerInput();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      title: '',
      start_number: 1,
    },
  });

  const [addTicketNumberSequence, { loading, error }] = useMutation(
    AddTicketNumberSequenceMutation,
    {
      variables: {
        input: {
          event: { id: event.id },
          info: form.values,
        },
      },
      onCompleted: ({ addTicketNumberSequence: sequence }) => {
        onSuccess?.(sequence);
        notifier.success(t('notifications.ticket_number_sequence_added'));
      },
      onError: () => {
        //
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addTicketNumberSequenceSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addTicketNumberSequence()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_sequence')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_ticket_number_sequence_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="SequenceTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('not_visible_to_participants')}
          </UI.InputDescription>
          <UI.DebouncedInput
            {...form.text('title')}
            maxLength={255}
            placeholder={t('ticket_number_sequence_title_placeholder')}
            autoFocus
            id="SequenceTitle"
          />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr 1fr'] }}>
          <UI.InputGroup
            sc={{ valid: !errors.prefix, touched: form.touched.prefix }}
          >
            <UI.InputLabel htmlFor="SequencePrefix">
              {t('prefix')}
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text('prefix')} maxLength={3} id="SequencePrefix" />
            <UI.ErrorMessages attribute={t('prefix')} errors={errors.prefix} />
          </UI.InputGroup>
          <UI.InputGroup
            sc={{ valid: !errors.start_number, touched: form.touched.start_number }}
          >
            <UI.InputLabel htmlFor="SequenceStartNumber">
              {t('start_number')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('start_number', {
                inputHandler: integerInput(),
              })}
              maxLength={8}
              inputMode="numeric"
              match={/[\d]/g}
              id="SequenceStartNumber"
            />
            <UI.ErrorMessages attribute={t('start_number')} errors={errors.start_number} />
          </UI.InputGroup>
          <UI.InputGroup
            sc={{ valid: !errors.suffix, touched: form.touched.suffix }}
          >
            <UI.InputLabel htmlFor="SequenceSuffix">
              {t('suffix')}
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text('suffix')} maxLength={3} id="SequenceSuffix" />
            <UI.ErrorMessages attribute={t('suffix')} errors={errors.suffix} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.corral_name, touched: form.touched.corral_name }}>
            <UI.InputLabel htmlFor="SequenceCorralName">
              {t('corral_name')}
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text('corral_name')} id="SequenceCorralName" />
            <UI.ErrorMessages attribute={t('corral_name')} errors={errors.corral_name} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.start_time, touched: form.touched.start_time }}>
            <UI.InputLabel htmlFor="SequenceStartTime">
              {t('start_time')}
            </UI.InputLabel>
            <UI.TimePicker
              selected={form.get('start_time')}
              onChange={(value) => form.set('start_time', value)}
              timeIntervals={5}
              id="SequenceStartTime"
            />
            <UI.ErrorMessages attribute={t('start_time')} errors={errors.start_time} />
          </UI.InputGroup>
          <UI.InputGroup sc={{ valid: !errors.capacity, touched: form.touched.capacity }}>
            <UI.InputLabel htmlFor="SequenceCapacity">
              {t('capacity')}
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('capacity', {
                inputHandler: integerInput(),
              })}
              format={integerInput().format}
              maxLength={8}
              inputMode="numeric"
              match={/[\d]/g}
              placeholder="∞"
              id="SequenceCapacity"
            />
            <UI.ErrorMessages attribute={t('capacity')} errors={errors.capacity} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_ticket_number_sequence')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddTicketNumberSequenceForm;
